import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { categorizeProduct } from "../lib/categorizeProduct";
import {
  isHeicFile,
  isWebpFile,
  isAvifFile,
  UNSUPPORTED_FORMAT_MESSAGE,
  UnsupportedImageFormatError,
  normalizeImageForUpload,
} from "../utils/heicConvert";
import {
  baseNameFromFileName,
  groupItemsByStem,
  type FileGroup,
} from "../utils/groupFilesByStem";
import {
  attachToDesdeGrupos,
  combinarConLaIa,
  computeUnits,
  entrarEnElLimite,
  esUnidadDeCarpeta,
  PREFIJO_CARPETA,
  type BulkUnit,
  type CarpetaDeItem,
  type GrupoIaRespuesta,
} from "../utils/bulkUnits";
import {
  avisosDelLote,
  limitarProductos,
  organizarPorCarpetas,
  type EntradaDeLote,
  type LotePorCarpetas,
} from "../lib/lotePorCarpetas";
import {
  ErrorDeLote,
  esZip,
  leerZipDeProductos,
} from "../lib/leerLoteDeProductos";
import { buildThumbnailBlob } from "../utils/thumbnail";
import {
  datosDeLaFicha,
  datosDelPdfDeLaUnidad,
  guardarDatosDelPdf,
  leerPdfDelProveedor,
  type DatosDelPdf,
} from "../lib/fichaDesdePdf";
import {
  MAX_IMAGENES_POR_PRODUCTO,
  cupoRestante,
  mensajeRecorte,
  recortarAlTope,
} from "../lib/limiteImagenesProducto";

export const BULK_MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB
export const BULK_CONCURRENCY = 3;

export type BulkItemStatus = "pending" | "uploading" | "success" | "error";

export type BulkItem = {
  id: string;
  file: File;
  previewUrl: string;
  nombre: string;
  status: BulkItemStatus;
  error?: string;
  /** La carpeta (de un .zip o soltada) de la que vino: sus fotos son un producto
   *  con el nombre de la carpeta, con cualquier modo del toggle. */
  carpeta?: CarpetaDeItem;
};

const nombreDeArchivo = (it: BulkItem) => it.file.name;
const carpetaDe = (it: BulkItem) => it.carpeta;

export const isFreeLikePlan = (plan: string) => {
  const p = (plan || "").toLowerCase();
  return p === "gratis" || p === "free" || p === "";
};

export const getBulkPlanLimit = (plan: string) =>
  isFreeLikePlan(plan) ? 5 : 20;

interface Args {
  idCompany: string | null;
  userPlan: string;
  onSuccess?: () => Promise<void> | void;
  brandId?: string | null;
}

export type SaveAllResult = "all-success" | "partial" | "none" | "error";

export function useBulkUpload({ idCompany, userPlan, onSuccess, brandId }: Args) {
  const [items, setItems] = useState<BulkItem[]>([]);
  const [startingN, setStartingN] = useState(1);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState<string | null>(null);
  const [convertingHeic, setConvertingHeic] = useState(false);
  // Abriendo un .zip o leyendo una carpeta soltada (antes de convertir las fotos).
  const [abriendoLote, setAbriendoLote] = useState(false);
  // El PDF del proveedor que se está leyendo (ADR-2026-10-18-el-pdf-entra-por-la-subida-de-siempre).
  const [leyendoPdf, setLeyendoPdf] = useState<string | null>(null);
  // Los datos de cada producto que vino de un PDF, por la key de su unidad: se guardan en la
  // ficha apenas se crea el producto.
  const datosDePdf = useRef(new Map<string, DatosDelPdf>());
  // Lo que un lote dejó afuera sin ser un error (fotos de más en una carpeta,
  // archivos que no son fotos): se avisa aparte, en gris.
  const [aviso, setAviso] = useState<string | null>(null);
  // Toggle "Usar nombres de archivo": cuando está activo, los items se
  // agrupan por stem (groupItemsByStem) y cada grupo se sube como un
  // producto distinto, con el primer archivo como imagen principal y el
  // resto como imágenes adicionales. Los nombres se prepoblan desde el
  // filename; el override por grupo se guarda en `groupNameOverrides`.
  const [useFileNames, setUseFileNamesRaw] = useState(false);
  const [groupNameOverrides, setGroupNameOverrides] = useState<
    Record<string, string>
  >({});
  // Merges manuales ("añadir esta imagen a otro producto"): itemId → key de
  // la unidad destino. Ver `computeUnits` para la semántica completa.
  const [attachTo, setAttachTo] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const limit = getBulkPlanLimit(userPlan);

  // Grupos derivados — sólo se calculan cuando el toggle está activo.
  const groups = useMemo<FileGroup<BulkItem>[]>(
    () =>
      useFileNames ? groupItemsByStem(items, (it) => it.file.name) : [],
    [items, useFileNames],
  );

  // Unidades = productos a crear (base por modo + merges manuales). Es lo
  // que la lista del modal muestra y lo que `saveAll` sube.
  const units = useMemo<BulkUnit<BulkItem>[]>(
    () => computeUnits(items, useFileNames, nombreDeArchivo, attachTo, carpetaDe),
    [items, useFileNames, attachTo],
  );

  // "Agrupar con IA" trabaja sólo sobre las fotos sueltas: las de una carpeta ya
  // vienen agrupadas por el usuario, y una suelta que él ya sumó a una carpeta, también.
  const itemsAgrupables = useMemo(
    () =>
      items.filter(
        (it) => !it.carpeta && !esUnidadDeCarpeta(attachTo[it.id] ?? ""),
      ),
    [items, attachTo],
  );

  // Togglear el modo cambia el significado de las keys destino (id de item
  // ↔ stem), así que los merges manuales no sobreviven al cambio: se
  // resetean y la lista vuelve a la agrupación derivada.
  const setUseFileNames = useCallback((value: boolean) => {
    setUseFileNamesRaw(value);
    setAttachTo({});
  }, []);

  /** Nombre a mostrar para un grupo: si el user lo editó, su override;
   *  caso contrario el `baseName` derivado del filename. */
  const groupDisplayName = useCallback(
    (group: FileGroup<BulkItem>) =>
      groupNameOverrides[group.key]?.trim() || group.baseName,
    [groupNameOverrides],
  );

  // Compute starting N whenever idCompany changes (so names default Nuevo producto N)
  useEffect(() => {
    if (!idCompany) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from("productos")
          .select("nombre")
          .eq("id_company", idCompany)
          .ilike("nombre", "Nuevo producto %");
        if (cancelled) return;
        const regex = /^Nuevo producto (\d+)$/;
        const maxN = (data || []).reduce((max: number, row: any) => {
          const name = (row?.nombre || "").trim();
          const m = regex.exec(name);
          if (!m) return max;
          const n = parseInt(m[1], 10);
          return Number.isFinite(n) && n > max ? n : max;
        }, 0);
        setStartingN(maxN + 1);
      } catch {
        setStartingN(1);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [idCompany]);

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Núcleo de normalización de archivos, compartido por todas las puertas de
  // entrada: file picker (handleSelectFiles), drag & drop (addFiles con
  // e.dataTransfer.files), un .zip o carpeta soltada (incorporarLote) y el "+"
  // de una fila (addFilesToUnit). Devuelve una posición por archivo (null si se
  // salteó) para que el lote por carpetas sepa de qué carpeta es cada foto.
  const normalizar = useCallback(
    async (
      incoming: FileList | File[],
    ): Promise<({ file: File; previewUrl: string } | null)[]> => {
      const rawList = Array.from(incoming);
      if (rawList.length === 0) return [];
      setError(null);
      // Spinner "Procesando imágenes…" solo si hay algo que convertir. AVIF
      // cuenta: la conversión por canvas de una foto de cámara moderna no es
      // instantánea.
      const hasConvertible = rawList.some(
        (f) => isHeicFile(f) || isWebpFile(f) || isAvifFile(f),
      );
      if (hasConvertible) setConvertingHeic(true);

      const processed: ({ file: File; previewUrl: string } | null)[] = [];
      for (const f of rawList) {
        processed.push(null);
        try {
          // `normalizeImageForUpload` es el ÚNICO lugar donde vive la política
          // de formatos: rechaza RAW/TIFF/GIF/SVG (ADR-590) y convierte
          // HEIC/HEIF, AVIF y WEBP (ADR-589) a JPEG. Antes esto se gateaba a
          // mano acá con `isHeicFile || isWebpFile`, y por eso el AVIF se subía
          // crudo (ADR-687).
          const ffile = await normalizeImageForUpload(f);
          // El preview se arma del archivo YA normalizado: así lo que se ve es
          // exactamente lo que se va a subir, y siempre es un formato que el
          // browser sabe pintar.
          const previewUrl = URL.createObjectURL(ffile);
          if (ffile.size > BULK_MAX_FILE_SIZE) {
            URL.revokeObjectURL(previewUrl);
            // Mismo criterio que el catch: nunca descartar en silencio.
            setError(
              `"${f.name}" supera el tamaño máximo de 100 MB y no se agregó.`,
            );
            continue;
          }
          processed[processed.length - 1] = { file: ffile, previewUrl };
        } catch (err) {
          // Se saltea ESE archivo y el resto de la tanda sigue — pero SIEMPRE
          // con mensaje al user: antes un fallo de conversión (ej. el decoder
          // del browser no banca AVIF) omitía la imagen en silencio y "desaparecía"
          // de la carga sin explicación.
          if (err instanceof UnsupportedImageFormatError) {
            setError(UNSUPPORTED_FORMAT_MESSAGE);
          } else {
            console.warn("Error procesando archivo, se omite:", f.name, err);
            setError(
              `No pudimos procesar "${f.name}" en este navegador. Convertila a JPG o PNG y volvé a intentar.`,
            );
          }
        }
      }
      setConvertingHeic(false);
      return processed;
    },
    [],
  );

  const processIncoming = useCallback(
    async (incoming: FileList | File[]) =>
      (await normalizar(incoming)).filter(
        (p): p is { file: File; previewUrl: string } => p !== null,
      ),
    [normalizar],
  );

  const buildItems = (
    processed: { file: File; previewUrl: string }[],
    carpeta?: CarpetaDeItem,
  ): BulkItem[] =>
    processed.map(({ file, previewUrl }, idx) => ({
      id: `${Date.now()}_${idx}_${Math.random().toString(36).slice(2, 8)}`,
      file,
      previewUrl,
      nombre: "",
      status: "pending" as const,
      ...(carpeta ? { carpeta } : {}),
    }));

  const mensajeDeLimite = useCallback(
    (fuera: number) =>
      `Llegaste al límite de ${limit} productos por carga: ${
        fuera === 1 ? "1 producto no se agregó" : `${fuera} productos no se agregaron`
      }.`,
    [limit],
  );

  /** Suma un lote al final de la lista. El límite de la carga cuenta PRODUCTOS
   *  (ADR-2026-10-16-subir-varios-con-zip-y-carpetas): una carpeta entra entera o
   *  no entra, y una foto suelta que cae en un grupo existente no suma. `omitidos`
   *  son productos que el lector ya dejó afuera por el mismo límite. */
  const incorporarLote = useCallback(
    async (
      lote: LotePorCarpetas<File>,
      omitidos = 0,
      avisoExtra: string | null = null,
      /** Los datos de las carpetas que vienen de un PDF, por su ruta. */
      datosPorRuta?: Map<string, DatosDelPdf>,
    ) => {
      const loteId = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const normalizados = await normalizar([
        ...lote.carpetas.flatMap((c) => c.archivos),
        ...lote.sueltas,
      ]);
      let cursor = 0;
      const tomar = (n: number) => {
        const parte = normalizados
          .slice(cursor, cursor + n)
          .filter((p): p is { file: File; previewUrl: string } => p !== null);
        cursor += n;
        return parte;
      };
      const bloques: BulkItem[][] = [];
      for (const c of lote.carpetas) {
        const fotos = tomar(c.archivos.length);
        if (fotos.length === 0) continue;
        bloques.push(buildItems(fotos, { key: `${loteId}/${c.ruta}`, nombre: c.nombre }));
        const datos = datosPorRuta?.get(c.ruta);
        if (datos) datosDePdf.current.set(`${PREFIJO_CARPETA}${loteId}/${c.ruta}`, datos);
      }
      for (const p of tomar(lote.sueltas.length)) bloques.push(buildItems([p]));

      const avisos = [...avisosDelLote(lote), ...(avisoExtra ? [avisoExtra] : [])];
      if (avisos.length > 0) setAviso(avisos.join(" "));
      if (bloques.length === 0) {
        if (omitidos > 0) setError(mensajeDeLimite(omitidos));
        return;
      }
      setItems((prev) => {
        const { aceptados, rechazados } = entrarEnElLimite(
          prev,
          bloques,
          (lista) =>
            computeUnits(lista, useFileNames, nombreDeArchivo, attachTo, carpetaDe)
              .length,
          limit,
        );
        rechazados.flat().forEach((it) => {
          if (it.previewUrl.startsWith("blob:")) URL.revokeObjectURL(it.previewUrl);
        });
        const fuera = rechazados.length + omitidos;
        if (fuera > 0) setError(mensajeDeLimite(fuera));
        return [...prev, ...aceptados];
      });
    },
    [normalizar, limit, useFileNames, attachTo, mensajeDeLimite],
  );

  /** Abre un .zip y suma sus productos (una carpeta = un producto). */
  const abrirZip = useCallback(
    async (zip: File) => {
      setAbriendoLote(true);
      try {
        // Se descomprimen como mucho `limit` productos: más no entran en la carga.
        const { lote, omitidos, pesadas } = await leerZipDeProductos(zip, limit);
        const avisoPesadas =
          pesadas > 0
            ? `${pesadas === 1 ? "1 foto pesa" : `${pesadas} fotos pesan`} más de 100 MB y no se ${pesadas === 1 ? "agregó" : "agregaron"}.`
            : null;
        if (lote.carpetas.length === 0 && lote.sueltas.length === 0 && omitidos === 0) {
          setError(
            avisoPesadas ??
              `"${zip.name}" no tiene fotos que podamos usar (JPG, PNG, WEBP, HEIC o AVIF).`,
          );
          return;
        }
        setAbriendoLote(false);
        await incorporarLote(lote, omitidos, avisoPesadas);
      } catch (err) {
        setError(
          err instanceof ErrorDeLote ? err.message : `No pudimos abrir "${zip.name}".`,
        );
      } finally {
        setAbriendoLote(false);
      }
    },
    [limit, incorporarLote],
  );

  /**
   * Lee el PDF del proveedor y lo suma como UN producto, como una carpeta de un .zip: con el nombre
   * y las fotos que eligió la IA, y sus datos guardados para la ficha
   * (ADR-2026-10-18-el-pdf-entra-por-la-subida-de-siempre).
   */
  const abrirPdf = useCallback(
    async (pdf: File) => {
      setLeyendoPdf(pdf.name);
      try {
        const { ficha, fotos, error: e } = await leerPdfDelProveedor(pdf);
        if (!ficha) {
          setError(`"${pdf.name}": ${e ?? "no pude leerlo."}`);
          return;
        }
        const porNumero = new Map(fotos.map((f) => [f.numero, f]));
        const elegidas = ficha.fotos.map((n) => porNumero.get(n)).filter((f): f is (typeof fotos)[number] => !!f);
        if (elegidas.length === 0) {
          setError(`No encontramos fotos del producto en "${pdf.name}". Subilo en "Un solo producto" para elegirlas a mano.`);
          return;
        }
        const ruta = `pdf:${pdf.name}:${Date.now()}`;
        const archivos = elegidas.map((f) => new File([f.blob], `pdf-p${f.pagina}-${f.numero}.jpg`, { type: "image/jpeg" }));
        const nombre = ficha.nombre || pdf.name.replace(/\.pdf$/i, "");
        await incorporarLote(
          { carpetas: [{ ruta, nombre, archivos, recortadas: 0 }], sueltas: [], ignorados: 0 },
          0,
          ficha.variosProductos ? `"${pdf.name}" parece tener varios productos: cargamos el principal.` : null,
          new Map([[ruta, datosDeLaFicha(ficha)]]),
        );
      } finally {
        setLeyendoPdf(null);
      }
    },
    [incorporarLote],
  );

  const addFiles = useCallback(
    async (incoming: FileList | File[]) => {
      const lista = Array.from(incoming);
      if (lista.length === 0) return;
      setAviso(null);
      const esPdf = (f: File) => f.type === "application/pdf" || /\.pdf$/i.test(f.name);
      const fotos = lista.filter((f) => !esZip(f) && !esPdf(f));
      if (fotos.length > 0)
        await incorporarLote({ carpetas: [], sueltas: fotos, ignorados: 0 });
      for (const zip of lista.filter(esZip)) await abrirZip(zip);
      for (const pdf of lista.filter(esPdf)) await abrirPdf(pdf);
    },
    [incorporarLote, abrirZip, abrirPdf],
  );

  /** Carpetas soltadas en escritorio: misma regla que el .zip. `leer` es lo que
   *  devolvió `capturarCarpetasSoltadas` en el onDrop. */
  const addCarpetas = useCallback(
    async (leer: () => Promise<EntradaDeLote<File>[]>) => {
      setAviso(null);
      setError(null);
      setAbriendoLote(true);
      let entradas: EntradaDeLote<File>[];
      try {
        entradas = await leer();
      } catch {
        setError("No pudimos leer la carpeta. Probá de nuevo o subila como .zip.");
        return;
      } finally {
        setAbriendoLote(false);
      }
      const { lote, omitidos } = limitarProductos(
        organizarPorCarpetas(entradas.filter((e) => !esZip(e.dato))),
        limit,
      );
      const zips = entradas.filter((e) => esZip(e.dato)).map((e) => e.dato);
      if (
        lote.carpetas.length === 0 &&
        lote.sueltas.length === 0 &&
        omitidos === 0 &&
        zips.length === 0
      ) {
        setError("La carpeta no tiene fotos que podamos usar (JPG, PNG, WEBP, HEIC o AVIF).");
        return;
      }
      await incorporarLote(lote, omitidos);
      for (const zip of zips) await abrirZip(zip);
    },
    [limit, incorporarLote, abrirZip],
  );

  /** Añade imágenes directamente a una unidad existente (el "+" de la
   *  galería de la fila). Los archivos nuevos entran como items `attachTo`
   *  la unidad → van al FINAL (la principal no cambia). Respeta el tope por
   *  producto (no suma productos, así que el límite de la carga no aplica);
   *  el recorte nunca es silencioso. */
  const addFilesToUnit = useCallback(
    async (key: string, incoming: FileList | File[]) => {
      const entrantes = Array.from(incoming).length;
      if (entrantes === 0) return;
      const unit = units.find((u) => u.key === key);
      if (!unit) return;
      const cupo = cupoRestante(unit.files.length);
      if (cupo <= 0) {
        setError(mensajeRecorte(entrantes));
        return;
      }
      const processed = await processIncoming(incoming);
      if (processed.length === 0) return;
      const toAdd = processed.slice(0, cupo);
      const discarded = processed.slice(cupo);
      discarded.forEach((d) => {
        if (d.previewUrl.startsWith("blob:")) URL.revokeObjectURL(d.previewUrl);
      });
      if (discarded.length > 0) setError(mensajeRecorte(discarded.length));
      if (toAdd.length === 0) return;
      const newItems = buildItems(toAdd);
      setItems((prev) => [...prev, ...newItems]);
      setAttachTo((prev) => {
        const next = { ...prev };
        for (const it of newItems) next[it.id] = key;
        return next;
      });
    },
    [units, processIncoming],
  );

  const handleSelectFiles = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      await addFiles(e.target.files || []);
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    [addFiles],
  );

  const removeItem = useCallback((id: string) => {
    setItems((prev) => {
      const it = prev.find((p) => p.id === id);
      if (it && it.previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(it.previewUrl);
      }
      return prev.filter((p) => p.id !== id);
    });
  }, []);

  const updateName = useCallback((id: string, name: string) => {
    setItems((prev) =>
      prev.map((p) => (p.id === id ? { ...p, nombre: name } : p)),
    );
  }, []);

  const defaultNameFor = useCallback(
    (index: number) => `Nuevo producto ${startingN + index}`,
    [startingN],
  );

  const clearAll = useCallback(() => {
    setItems((prev) => {
      prev.forEach((it) => {
        if (it.previewUrl.startsWith("blob:"))
          URL.revokeObjectURL(it.previewUrl);
      });
      return [];
    });
    setProgress({ done: 0, total: 0 });
    setError(null);
    setAviso(null);
    setGroupNameOverrides({});
    setUseFileNames(false);
    aiSnapshotRef.current = null;
  }, []);

  /** Versión optimizada para el bucket `opt/`. Best-effort: null si falla. */
  const buildOptimizedBlob = useCallback(
    (file: File): Promise<Blob | null> => buildThumbnailBlob(file),
    [],
  );

  /** Sube un producto con N archivos. El primero queda como imagen
   *  principal; los siguientes se insertan en `productos_imagenes` con
   *  `principal: false` y `orden` incremental. Si UNO de los archivos
   *  falla en subir, todo el producto se marca como error (el row de
   *  producto/variante queda creado pero sin imágenes — el user verá
   *  el row con error y podrá reintentar). */
  const uploadProductWithFiles = useCallback(
    async (
      files: File[],
      effectiveName: string,
      userId: string,
    ): Promise<{ ok: boolean; error?: string; varianteId?: string }> => {
      try {
        if (!idCompany) throw new Error("No se encontró la empresa");
        if (files.length === 0) throw new Error("Sin archivos");
        // Red de contención del tope: el llamador ya recortó y avisó, pero esta
        // función está expuesta en el return del hook y crea el producto de cero.
        const aSubir = recortarAlTope(0, files).aceptadas;

        const productoPayload: Record<string, any> = {
          id_company: idCompany,
          nombre: effectiveName,
          tiene_variantes: false,
        };
        if (brandId) {
          productoPayload.brand_id = brandId;
        }
        const { data: producto, error: productoError } = await supabase
          .from("productos")
          .insert(productoPayload)
          .select("id")
          .single();
        if (productoError) throw productoError;
        const productId = String(producto.id);

        const { data: variant, error: variantError } = await supabase
          .from("variantes")
          .insert({
            id_company: idCompany,
            nombre: effectiveName,
            activo: true,
            product_id: productId,
          })
          .select("id")
          .single();
        if (variantError) throw variantError;
        const variantId = variant.id as string;

        const timestamp = Date.now();
        // Subimos archivos secuencialmente para mantener nombres únicos y
        // ordenar el `orden` de manera predecible. El timestamp + el idx
        // evita colisiones si dos archivos comparten nombre exacto.
        for (let i = 0; i < aSubir.length; i++) {
          const file = aSubir[i];
          const sanitizedName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
          const fileBaseName = `${timestamp}_${i}_${sanitizedName}`;
          const fullPath = `${idCompany}/${variantId}/full/${fileBaseName}`;
          const { error: fullErr } = await supabase.storage
            .from("productos")
            .upload(fullPath, file);
          if (fullErr) throw fullErr;

          const optBlob = await buildOptimizedBlob(file);
          if (optBlob) {
            const optPath = `${idCompany}/${variantId}/opt/${fileBaseName}`;
            const { error: optErr } = await supabase.storage
              .from("productos")
              .upload(optPath, optBlob);
            if (optErr)
              console.warn("Optimized upload failed for", fileBaseName, optErr);
          }

          const { error: imgErr } = await supabase
            .from("productos_imagenes")
            .insert({
              id_company: idCompany,
              variante_id: variantId,
              url: fileBaseName,
              principal: i === 0,
              orden: i + 1,
            });
          if (imgErr) throw imgErr;
        }

        categorizeProduct(String(productId), {
          modo: "crear",
          completarNombre: false,
          userId,
        });

        return { ok: true, varianteId: variantId };
      } catch (err: any) {
        return {
          ok: false,
          error: err?.message || "Error al guardar el producto",
        };
      }
    },
    [idCompany, brandId, buildOptimizedBlob],
  );

  /** Edita el nombre que se va a usar para un grupo (solo aplica cuando
   *  el toggle "Usar nombres de archivo" está activo). */
  const updateGroupName = useCallback((key: string, name: string) => {
    setGroupNameOverrides((prev) => ({ ...prev, [key]: name }));
  }, []);

  /** Elimina todos los items pertenecientes a un grupo. */
  const removeGroup = useCallback((key: string) => {
    setGroupNameOverrides((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setItems((prev) => {
      // Recalculamos los grupos sobre la lista actual y filtramos los
      // items que pertenecen a la `key` provista. Hacerlo así (vs guardar
      // el key dentro del item) nos evita desincronización si el set de
      // items cambia entre renders.
      const allGroups = groupItemsByStem(prev, (it) => it.file.name);
      const target = allGroups.find((g) => g.key === key);
      const drop = new Set(target?.files.map((it) => it.id) || []);
      const removed = prev.filter((p) => drop.has(p.id));
      removed.forEach((it) => {
        if (it.previewUrl.startsWith("blob:")) URL.revokeObjectURL(it.previewUrl);
      });
      return prev.filter((p) => !drop.has(p.id));
    });
  }, []);

  // ── Operaciones sobre unidades (la lista del modal opera con esto) ──

  /** Nombre a mostrar de una unidad: en modo stem (o si vino de una carpeta)
   *  el override del user o el baseName; en modo plano el nombre del item host
   *  ("" si no lo editó — el caller pone el placeholder). */
  const unitDisplayName = useCallback(
    (unit: BulkUnit<BulkItem>) =>
      useFileNames || esUnidadDeCarpeta(unit.key)
        ? groupNameOverrides[unit.key]?.trim() || unit.baseName || ""
        : unit.files[0]?.nombre ?? "",
    [useFileNames, groupNameOverrides],
  );

  /** Renombra una unidad por su key, sea cual sea el modo. */
  const renameUnit = useCallback(
    (key: string, name: string) => {
      if (useFileNames || esUnidadDeCarpeta(key)) updateGroupName(key, name);
      else updateName(key, name); // modo plano: key = id del item host
    },
    [useFileNames, updateGroupName, updateName],
  );

  /** Elimina una unidad completa (host + imágenes añadidas manualmente). */
  const removeUnit = useCallback(
    (key: string) => {
      const target = units.find((u) => u.key === key);
      if (!target) return;
      const drop = new Set(target.files.map((f) => f.id));
      target.files.forEach((f) => {
        if (f.previewUrl.startsWith("blob:")) URL.revokeObjectURL(f.previewUrl);
      });
      setItems((prev) => prev.filter((p) => !drop.has(p.id)));
      setAttachTo((prev) => {
        const next: Record<string, string> = {};
        for (const [id, to] of Object.entries(prev)) {
          if (!drop.has(id)) next[id] = to;
        }
        return next;
      });
      if (useFileNames || esUnidadDeCarpeta(key)) {
        setGroupNameOverrides((prev) => {
          if (!(key in prev)) return prev;
          const next = { ...prev };
          delete next[key];
          return next;
        });
      }
    },
    [units, useFileNames],
  );

  /** Añade TODAS las imágenes de la unidad `sourceKey` al final de la unidad
   *  `targetKey`. Devuelve un mensaje de error si no se puede (tope de
   *  imágenes por producto), o null si se aplicó. */
  const mergeUnits = useCallback(
    (sourceKey: string, targetKey: string): string | null => {
      if (sourceKey === targetKey) return null;
      const source = units.find((u) => u.key === sourceKey);
      const target = units.find((u) => u.key === targetKey);
      if (!source || !target) return "Ese producto ya no está en la lista.";
      if (
        source.files.length + target.files.length >
        MAX_IMAGENES_POR_PRODUCTO
      ) {
        return `Máximo ${MAX_IMAGENES_POR_PRODUCTO} imágenes por producto: este ya no tiene lugar.`;
      }
      // Se reasignan TODOS los archivos visibles de la unidad origen (host +
      // ya añadidos): así nunca quedan cadenas A→B→C que resolver después.
      setAttachTo((prev) => {
        const next = { ...prev };
        for (const f of source.files) next[f.id] = targetKey;
        return next;
      });
      return null;
    },
    [units],
  );

  // ── "Agrupar con IA" ──────────────────────────────────────────────────
  // Snapshot de lo que la aplicación pisa (agrupación + nombres), para el
  // "Deshacer". Se reemplaza en cada aplicación nueva y se limpia en clearAll.
  const aiSnapshotRef = useRef<{
    attachTo: Record<string, string>;
    useFileNames: boolean;
    groupNameOverrides: Record<string, string>;
    /** Nombre PREVIO de cada item al que se le escribió el sugerido. */
    nombres: Record<string, string>;
  } | null>(null);

  /** Aplica los grupos que devolvió el backend: pasa a modo plano, arma el
   *  attachTo y completa nombres sugeridos SOLO donde el usuario no escribió
   *  uno. Devuelve la cantidad de productos resultantes. */
  const applyAiGrouping = useCallback(
    (grupos: GrupoIaRespuesta[]): number => {
      const { attachTo: deLaIa, nombres } = attachToDesdeGrupos(
        itemsAgrupables.map((it) => it.id),
        grupos,
        MAX_IMAGENES_POR_PRODUCTO,
      );
      const nuevo = combinarConLaIa(
        attachTo,
        deLaIa,
        new Set(itemsAgrupables.map((it) => it.id)),
        MAX_IMAGENES_POR_PRODUCTO,
      );
      const nombresPrevios: Record<string, string> = {};
      const nombresAplicar: Record<string, string> = {};
      for (const it of items) {
        const sugerido = nombres[it.id];
        if (sugerido && !it.nombre.trim()) {
          nombresPrevios[it.id] = it.nombre;
          nombresAplicar[it.id] = sugerido;
        }
      }
      aiSnapshotRef.current = {
        attachTo,
        useFileNames,
        groupNameOverrides,
        nombres: nombresPrevios,
      };
      setUseFileNamesRaw(false);
      setAttachTo(nuevo);
      if (Object.keys(nombresAplicar).length > 0) {
        setItems((prev) =>
          prev.map((p) =>
            nombresAplicar[p.id] != null ? { ...p, nombre: nombresAplicar[p.id] } : p,
          ),
        );
      }
      return computeUnits(items, false, nombreDeArchivo, nuevo, carpetaDe).length;
    },
    [items, itemsAgrupables, attachTo, useFileNames, groupNameOverrides],
  );

  /** Restaura agrupación y nombres previos a la última aplicación de IA. */
  const undoAiGrouping = useCallback(() => {
    const snap = aiSnapshotRef.current;
    if (!snap) return;
    aiSnapshotRef.current = null;
    setUseFileNamesRaw(snap.useFileNames);
    setGroupNameOverrides(snap.groupNameOverrides);
    setAttachTo(snap.attachTo);
    setItems((prev) =>
      prev.map((p) =>
        snap.nombres[p.id] != null ? { ...p, nombre: snap.nombres[p.id] } : p,
      ),
    );
  }, []);

  const descartarAviso = useCallback(() => setAviso(null), []);

  const saveAll = useCallback(async (): Promise<SaveAllResult> => {
    if (items.length === 0 || saving) return "none";
    setSaving(true);
    setError(null);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No se encontró el usuario");

      // Recalculate startingN at save time to avoid collisions
      let baseN = startingN;
      try {
        const { data } = await supabase
          .from("productos")
          .select("nombre")
          .eq("id_company", idCompany)
          .ilike("nombre", "Nuevo producto %");
        const regex = /^Nuevo producto (\d+)$/;
        const maxN = (data || []).reduce((max: number, row: any) => {
          const name = (row?.nombre || "").trim();
          const m = regex.exec(name);
          if (!m) return max;
          const n = parseInt(m[1], 10);
          return Number.isFinite(n) && n > max ? n : max;
        }, 0);
        baseN = maxN + 1;
        setStartingN(baseN);
      } catch {}

      // Snapshot del modo actual: si el user togglea durante el save no
      // pasa nada — usamos lo que había al apretar Guardar.
      const savingByGroups = useFileNames;
      // Cada "task" representa un producto a crear (1+ archivos). Se arma
      // desde las MISMAS unidades que muestra la lista (base por modo +
      // merges manuales): lo que se ve es exactamente lo que se sube.
      type SaveTask = { itemIds: string[]; files: File[]; name: string; key: string; carpetas: Array<string | undefined> };
      // Tope de imágenes por producto (MAX_IMAGENES_POR_PRODUCTO). Los archivos que sobran NO se
      // cuelan en el task: quedan afuera con su propio error. Meterlos en
      // `itemIds` los borraría de la lista al terminar OK —el usuario los
      // vería desaparecer creyendo que se subieron—, y que se suban igual es
      // justamente lo que el tope prohíbe.
      const itemsRecortados: string[] = [];
      const tasks: SaveTask[] = computeUnits(
        items,
        savingByGroups,
        nombreDeArchivo,
        attachTo,
        carpetaDe,
      ).map((unit, idx) => {
        const { aceptadas, rechazadas } = recortarAlTope(0, unit.files);
        itemsRecortados.push(...rechazadas.map((it) => it.id));
        const name = savingByGroups || esUnidadDeCarpeta(unit.key)
          ? groupNameOverrides[unit.key]?.trim() || unit.baseName || ""
          : unit.files[0]?.nombre.trim() || "";
        return {
          itemIds: aceptadas.map((it) => it.id),
          files: aceptadas.map((it) => it.file),
          name: name || `Nuevo producto ${baseN + idx}`,
          key: unit.key,
          // De qué carpeta (o PDF) vino cada foto: encuentra los datos del PDF aunque se haya juntado.
          carpetas: aceptadas.map((it) => (it.carpeta ? `${PREFIJO_CARPETA}${it.carpeta.key}` : undefined)),
        };
      });

      setProgress({ done: 0, total: tasks.length });

      const taskIds = new Set(tasks.flatMap((t) => t.itemIds));
      setItems((prev) =>
        prev.map((p) =>
          taskIds.has(p.id)
            ? { ...p, status: "uploading" as const, error: undefined }
            : p,
        ),
      );

      const queue = [...tasks];
      let done = 0;
      const results: { itemIds: string[]; ok: boolean; error?: string }[] = [];

      const worker = async () => {
        while (queue.length > 0) {
          const task = queue.shift();
          if (!task) return;
          const res = await uploadProductWithFiles(
            task.files,
            task.name,
            user.id,
          );
          // Si vino de un PDF, su ficha. Si falla, el producto ya quedó: se completa desde Mi marca.
          const delPdf = datosDelPdfDeLaUnidad(task.key, task.carpetas, datosDePdf.current);
          if (res.ok && res.varianteId && delPdf) {
            const texto = await guardarDatosDelPdf(res.varianteId, task.name, delPdf.datos);
            if (/No guardé/.test(texto)) console.warn("[PDF del proveedor]", texto);
            datosDePdf.current.delete(delPdf.clave);
          }
          done += 1;
          setProgress({ done, total: tasks.length });
          const idSet = new Set(task.itemIds);
          setItems((prev) =>
            prev.map((p) =>
              idSet.has(p.id)
                ? {
                    ...p,
                    status: res.ok ? "success" : "error",
                    error: res.error,
                  }
                : p,
            ),
          );
          results.push({ itemIds: task.itemIds, ok: res.ok, error: res.error });
        }
      };

      const workers = Array.from(
        { length: Math.min(BULK_CONCURRENCY, tasks.length) },
        () => worker(),
      );
      await Promise.all(workers);

      // Los recortados por el tope entran al mismo circuito que un fallo: quedan
      // marcados en la lista con el motivo, y la rama "partial" de abajo los deja
      // `pending` para que el usuario los borre o los guarde como otro producto.
      const avisoTope = mensajeRecorte(itemsRecortados.length);
      if (avisoTope) {
        const recortadosSet = new Set(itemsRecortados);
        setItems((prev) =>
          prev.map((p) =>
            recortadosSet.has(p.id)
              ? { ...p, status: "error" as const, error: avisoTope }
              : p,
          ),
        );
        results.push({ itemIds: itemsRecortados, ok: false, error: avisoTope });
      }

      const successCount = results.filter((r) => r.ok).length;
      const failCount = results.length - successCount;

      try {
        if (onSuccess) await onSuccess();
      } catch (e) {
        console.warn("onSuccess refresh failed:", e);
      }

      if (failCount === 0) {
        return "all-success";
      } else {
        setError(
          `${successCount} producto(s) creado(s). ${failCount} fallaron — revisalos abajo y reintentá.`,
        );
        // Eliminar items de los productos que se subieron OK, y dejar
        // pending los items que pertenecen a productos que fallaron para
        // que el user pueda reintentar.
        const okItemIds = new Set(
          results.filter((r) => r.ok).flatMap((r) => r.itemIds),
        );
        setItems((prev) =>
          prev
            .filter((p) => !okItemIds.has(p.id))
            .map((p) => ({ ...p, status: "pending" as const })),
        );
        setProgress({ done: 0, total: 0 });
        return "partial";
      }
    } catch (err: any) {
      setError(err?.message || "Error inesperado al guardar");
      return "error";
    } finally {
      setSaving(false);
    }
  }, [
    items,
    saving,
    idCompany,
    startingN,
    useFileNames,
    groupNameOverrides,
    attachTo,
    uploadProductWithFiles,
    onSuccess,
  ]);

  return {
    items,
    limit,
    saving,
    progress,
    error,
    /** Lo que un .zip o carpeta dejó afuera sin ser error (se muestra en gris). */
    aviso,
    descartarAviso,
    // También cubre abrir un .zip o leer una carpeta: para la pantalla es el
    // mismo "preparando las fotos" (botones deshabilitados, abanico animado).
    convertingHeic: convertingHeic || abriendoLote || leyendoPdf !== null,
    /** El PDF del proveedor que se está leyendo (tarda ~15 s), para decirlo. */
    leyendoPdf,
    fileInputRef,
    startingN,
    openFilePicker,
    addFiles,
    addCarpetas,
    handleSelectFiles,
    removeItem,
    updateName,
    defaultNameFor,
    clearAll,
    saveAll,
    isFreeLikePlan: isFreeLikePlan(userPlan),
    // ── Modo "Usar nombres de archivo" ────────────────────────────────
    useFileNames,
    setUseFileNames,
    groups,
    groupDisplayName,
    updateGroupName,
    removeGroup,
    // ── Unidades (lista unificada + "añadir a otro producto") ─────────
    units,
    unitDisplayName,
    renameUnit,
    removeUnit,
    mergeUnits,
    addFilesToUnit,
    applyAiGrouping,
    undoAiGrouping,
    itemsAgrupables,
    /** Helper exportado por si el caller quiere prepoblar/derivar el
     *  baseName desde un filename sin armar todo un grupo. */
    baseNameFromFileName,
  };
}
